import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DataTable, Panel, StatusChip, type DataTableColumn, type DateRange } from '../../../ui-kit';
import TableSectionActions, { TableSectionTools } from './TableSectionActions';
import { useTableSection } from '../useTableSection';
import { formatValue } from '../format';
import type {
  BreakdownRow, BreakdownTableBlockDef, ManualDashboardPayload,
} from '../../../types/manualDashboard';

// Column widths, px. The row-name column is sticky, so it needs a fixed width.
const ROW_LABEL_WIDTH = 200;
const MEASURE_MIN_WIDTH = 124;

interface BreakdownTablePanelProps {
  dashboardId?: string;
  block: BreakdownTableBlockDef;
  payload: ManualDashboardPayload;
  range: DateRange;
  filters: Record<string, string[]>;
  refreshNonce: number;
  loading: boolean;
  collapsible?: boolean;
  loadingHeight?: number;
  onGrainChange?: (blockId: string, grain: string | undefined) => void;
}

/**
 * Rows by a dimension — carriers — with a pair of measures under each period
 * heading: "Target premium" and "Excess premium" under May, June, July.
 * Newest period first, like the board's other tables. A period still under
 * way is greyed: its statements are not all in.
 */
export default function BreakdownTablePanel({
  dashboardId,
  block,
  payload,
  range,
  filters,
  refreshNonce,
  loading,
  collapsible = false,
  loadingHeight = 200,
  onGrainChange,
}: BreakdownTablePanelProps) {
  const section = useTableSection({
    dashboardId, blockId: block.id, payload, range, filters, refreshNonce, onGrainChange,
  });
  const view =
    (section.source.blocks.find((b) => b.type === 'breakdown_table' && b.id === block.id) as
      | BreakdownTableBlockDef
      | undefined) ?? block;

  const newestFirst = React.useMemo(() => [...section.source.periods].reverse(), [section.source.periods]);
  const provisional = new Set(view.provisional_periods ?? []);

  const columns: DataTableColumn<BreakdownRow>[] = [
    {
      key: '__row',
      header: view.row_label,
      width: ROW_LABEL_WIDTH,
      sticky: true,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: row.total ? 600 : 500 }}>
          {row.label}
        </Typography>
      ),
    },
    ...newestFirst.flatMap((period) =>
      view.measures.map((measure) => ({
        key: `${period.key}:${measure.key}`,
        group: period.label,
        header: measure.label,
        numeric: true,
        // Centred under the month heading they belong to, so each month
        // reads as one block rather than figures drifting to the right.
        align: 'center' as const,
        minWidth: MEASURE_MIN_WIDTH,
        render: (row: BreakdownRow) => {
          const value = row.values[period.key]?.[measure.key] ?? null;
          const text = formatValue(value, measure.format);
          if (value === null || !provisional.has(period.key)) return text;
          return (
            <Tooltip title="This period is not over yet, so its statements are not all in">
              <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {text}
              </Box>
            </Tooltip>
          );
        },
      })),
    ),
  ];

  const definitions = view.measures.filter((m) => m.definition);

  // Drawn twice when expanded — in the panel and full screen — so built once.
  const table = (fill: boolean) => (
    <DataTable
      ariaLabel={view.title ?? 'Breakdown'}
      columns={columns}
      groups={[{ key: 'rows', title: '', rows: view.rows }]}
      getRowKey={(row) => row.key}
      fill={fill}
      // Empty usually means the filters kept only carriers that send no
      // premium statements, which reads as broken unless it is said.
      emptyMessage={
        Object.values(filters).some((v) => v.length)
          ? 'No premium statements for this selection. Only carriers that send premium statements appear here, so filtering to any other carrier leaves this table empty.'
          : 'No premium statements for this range.'
      }
    />
  );

  return (
    <Panel
      title={view.work_in_progress ? (
        <Stack direction="row" spacing={1} alignItems="center" component="span" sx={{ display: 'inline-flex' }}>
          <span>{view.title}</span>
          <StatusChip tone="warning" label="Work in progress" plain />
        </Stack>
      ) : view.title}
      subtitle={view.subtitle}
      info={
        definitions.length ? (
          <Stack spacing={0.75} sx={{ py: 0.25 }}>
            {definitions.map((m) => (
              <Typography key={m.key} variant="caption" sx={{ display: 'block' }}>
                <strong>{m.label}:</strong> {m.definition}
              </Typography>
            ))}
          </Stack>
        ) : undefined
      }
      tools={<TableSectionTools section={section} />}
      actions={<TableSectionActions section={section} disabled={loading} />}
      loading={loading || section.regrouping}
      loadingHeight={loadingHeight}
      collapsible={collapsible}
      flush
      footer={section.problem ?? view.note ?? null}
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
